import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  radius: '16px',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const PageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 4, md: 5 }, mt: { xs: 6, sm: 7, md: 0 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 'bold',
        color: ui.text,
        letterSpacing: 0.5,
        fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2.125rem' },
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          backgroundColor: ui.primary,
          height: '4px',
          width: { xs: '80px', sm: '100px' },
          position: 'absolute',
          left: 0,
          bottom: '-8px',
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const NavCard = ({ to, icon: Icon, title }) => (
  <Link to={to} style={{ textDecoration: 'none', flexGrow: 1, minWidth: 0 }}>
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        p: { xs: 2.5, md: 3.5 },
        borderRadius: ui.radius,
        border: `1px solid ${ui.border}`,
        backgroundColor: '#fff',
        boxShadow: ui.shadow,
        transition: 'all 0.2s ease',
        '& .nav-arrow': { transition: 'transform 0.2s ease' },
        '&:hover': {
          borderColor: ui.primary,
          boxShadow: '0 8px 28px rgba(0, 157, 185, 0.15)',
          '& .nav-arrow': { transform: 'translateX(4px)' },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 56,
          height: 56,
          borderRadius: '14px',
          backgroundColor: ui.primarySoft,
          color: ui.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 30 }} />
      </Box>
      <Typography
        sx={{
          flexGrow: 1,
          fontWeight: 700,
          color: ui.text,
          fontSize: { xs: '1.1rem', md: '1.25rem' },
        }}
      >
        {title}
      </Typography>
      <ArrowForwardIcon className="nav-arrow" sx={{ color: ui.primary, flexShrink: 0 }} />
    </Box>
  </Link>
);

const ManagerTeamMetrics = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Toggles the mobile drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar />
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: "70px", sm: "50px", md: "120px" },
            backgroundColor: '#fff',
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, md: 3 },
            position: 'relative',
          }}
        >
          {/* Fixed Hamburger Menu for Mobile - Positioned below navbar */}
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'fixed',
              top: { xs: 70, sm: 72 },
              left: { xs: 12, sm: 16 },
              zIndex: 1100,
              mb: 2,
            }}
          >
            <Tooltip title="Menu" arrow>
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  background: 'linear-gradient(135deg, #009db9 0%, #007a8a 100%)',
                  color: '#fff',
                  borderRadius: '12px',
                  p: 1.2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #007a8a 0%, #005b66 100%)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
                aria-label="toggle sidebar"
              >
                <MenuIcon sx={{ fontSize: '1.6rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          <PageHeader title="Manager Team Administration" />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, md: 3 },
              mt: { xs: 4, md: 6 },
            }}
          >
            {/* View Metrics */}
            <NavCard to="/view-metrics" icon={BarChartOutlinedIcon} title="View Metrics" />

            {/* Validate Metrics */}
            <NavCard to="/validate-metrics" icon={FactCheckOutlinedIcon} title="Validate Metrics" />
          </Box>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: 1200,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            top: { xs: 0, sm: 0 },
            height: '100%',
            borderRight: 'none',
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>
    </Box>
  );
};

export default ManagerTeamMetrics;